import { useFetchFilteredMovies } from "@multicines/services";
import { Language, Modality, MovieFormat } from "@multicines/types";
import { titleCase } from "@multicines/utils";
import { Modal } from "artisn-ui-react";
import CheckSVG from "images/check.svg";
import CloseSVG from "images/close.svg";
import React, { useCallback } from "react";

import { getFormats, getLanguage } from "./BillboardFilters.helpers";
import { getLanguages, getModalities } from "./BillboardFilters.helpers";
import { getModality } from "./BillboardFilters.helpers";
import { BillboardFiltersHeaderStyled } from "./BillboardFilters.styles";
import Styles from "./BillboardFilters.styles";
import { BillboardFiltersProps as Props } from "./BillboardFilters.types";
import Button from "components/global/Button/Button";
import Divider from "components/global/Divider/Divider";
import useI18n from "hooks/useI18n";
import { useAuthStore } from "stores/auth/auth.store";
import { useGlobalsStore } from "stores/globals/globals.store";
import { useStoresStore } from "stores/stores/stores.store";

const BillboardFilters: React.FC<Props> = props => {
  const { opened, setOpened } = props;
  const isAnonymous = useAuthStore(state => state.isAnonymous);
  const uid = useAuthStore(state => state.uid);
  const t = useI18n();
  const selectedStore = useStoresStore(store => store.selectedStore);
  const setModalityFilter = useGlobalsStore(state => state.setModalityFilter);
  const setLanguageFilter = useGlobalsStore(state => state.setLanguageFilter);
  const setTheaterFilter = useGlobalsStore(state => state.setTheaterFilter);
  const setFormatFilter = useGlobalsStore(state => state.setFormatFilter);
  const modalityFilter = useGlobalsStore(state => state.modalityFilter);
  const languageFilter = useGlobalsStore(state => state.languageFilter);
  const theaterFilter = useGlobalsStore(state => state.theaterFilter);
  const formatFilter = useGlobalsStore(state => state.formatFilter);
  const resetFilters = useGlobalsStore(state => state.resetFilters);
  const { data: movies } = useFetchFilteredMovies(
    { isAnonymous, uid },
    modalityFilter,
    selectedStore
  );

  const availableFilters = movies?.some(
    movie => movie.format_types && movie.theater_types
  );

  const theatersArrays = movies?.map(item => item.theater_types);
  const theatersArray = new Set(theatersArrays?.flat());
  const theaters = ["All", ...Array.from(theatersArray)];

  const modalHeaderNode = (
    <BillboardFiltersHeaderStyled>
      <p className="BillboardFilters__title">{t.filters.title}</p>
      <Button
        mode="PRIMARY"
        className="BillboardFilters__reset"
        onClick={resetFilters}
      >
        {t.filters.reset}
      </Button>
    </BillboardFiltersHeaderStyled>
  );

  const renderModality = useCallback(
    (listItem: Modality) => {
      const modality = listItem;
      const storeSelected =
        modalityFilter === modality
          ? "BillboardFilters__filters__selected"
          : [];
      const selectedCheck = modalityFilter === modality ? <CheckSVG /> : null;
      return (
        <>
          <Button
            mode="CUSTOM"
            className={`BillboardFilters__filters ${storeSelected}`}
            onClick={() => setModalityFilter(listItem)}
          >
            <div className="BillboardFilters__name__container">
              <p className="BillboardFilters__name">
                {getModality(modality, t.filters)}
              </p>
            </div>
            <div className="BillboardFilters__selected">{selectedCheck}</div>
          </Button>
          <Divider className="BillboardFilters__line" />
        </>
      );
    },
    [modalityFilter, setModalityFilter, t.filters]
  );

  const renderLanguages = useCallback(
    (listItem: Language) => {
      const storeSelected =
        languageFilter === listItem
          ? "BillboardFilters__filters__selected"
          : [];
      const selectedCheck = languageFilter === listItem ? <CheckSVG /> : null;

      return (
        <>
          <Button
            mode="CUSTOM"
            className={`BillboardFilters__filters ${storeSelected}`}
            onClick={() => setLanguageFilter(listItem)}
          >
            <div className="BillboardFilters__name__container">
              <p className="BillboardFilters__name">
                {getLanguage(listItem, t.filters)}
              </p>
            </div>
            <div className="BillboardFilters__selected">{selectedCheck}</div>
          </Button>
          <Divider className="BillboardFilters__line" />
        </>
      );
    },
    [languageFilter, t.filters, setLanguageFilter]
  );

  const renderFormats = useCallback(
    (listItem: MovieFormat) => {
      const storeSelected =
        formatFilter === listItem ? "BillboardFilters__filters__selected" : [];
      const selectedCheck = formatFilter === listItem ? <CheckSVG /> : null;

      return (
        <>
          <Button
            mode="CUSTOM"
            className={`BillboardFilters__filters ${storeSelected}`}
            onClick={() => setFormatFilter(listItem)}
          >
            <div className="BillboardFilters__name__container">
              <p className="BillboardFilters__name">
                {listItem === "All" ? t.filters.all : titleCase(listItem)}
              </p>
            </div>
            <div className="BillboardFilters__selected">{selectedCheck}</div>
          </Button>
          <Divider className="BillboardFilters__line" />
        </>
      );
    },
    [formatFilter, setFormatFilter, t.filters.all]
  );

  return (
    <Modal
      opened={opened}
      color="transparent"
      className="BillboardFilters__modal"
      header={modalHeaderNode}
      closeIcon={<CloseSVG />}
      onClose={() => setOpened(false)}
    >
      <Styles className="BillboardFilters">
        <p className="BillboardFilters__subtitle">{t.filters.view}</p>
        {getModalities().map((item, index) => {
          return <div key={index}>{renderModality(item)}</div>;
        })}
        <p className="BillboardFilters__subtitle">{t.filters.languages}</p>
        {getLanguages()?.map((item, index) => {
          return <div key={index}>{renderLanguages(item)}</div>;
        })}

        {availableFilters ? (
          <>
            <p className="BillboardFilters__subtitle">{t.filters.type}</p>
            {theaters?.map((theater, index) => {
              return (
                <div key={index}>
                  <Button
                    mode="CUSTOM"
                    className={`BillboardFilters__filters ${
                      theaterFilter === theater
                        ? "BillboardFilters__filters__selected"
                        : []
                    }`}
                    onClick={() => setTheaterFilter(theater)}
                  >
                    <div className="BillboardFilters__name__container">
                      <p className="BillboardFilters__name">
                        {theater === "All" ? t.filters.all : titleCase(theater)}
                      </p>
                    </div>
                    <div className="BillboardFilters__selected">
                      {theaterFilter === theater ? <CheckSVG /> : null}
                    </div>
                  </Button>
                  <Divider className="BillboardFilters__line" />
                </div>
              );
            })}
          </>
        ) : null}

        <p className="BillboardFilters__subtitle">{t.filters.format}</p>
        {getFormats()?.map((item, index) => {
          return <div key={index}>{renderFormats(item)}</div>;
        })}

        <Button
          mode="PRIMARY"
          className="BillboardFilters__button"
          onClick={() => setOpened(false)}
        >
          {t.common.apply}
        </Button>
      </Styles>
    </Modal>
  );
};

export default BillboardFilters;
